// Word Translate & Lookup: pure search/matching logic over a pre-built
// EN↔PT dictionary. No React, no network, no LLM — everything here is a
// lookup over a static dataset assembled ahead of time, per the PRD.
//
// data/dictionary.json is a build artifact — regenerate it, never hand-edit
// it. The source of truth is script/build_dictionary.py, which emits one
// hand-authored sense (pos / gender / gloss / example) for every verb in
// verbs.json and every word or phrase in the A1 vocabulary lists ("Phase 1":
// the vocabulary the rest of the app already teaches). To add words, edit
// that script and run `python3 script/build_dictionary.py`.

import dictionaryData from '../../data/dictionary.json'
import { conjugationGridFor, extraVerbForms } from './conjugator'

export type PartOfSpeech =
  | 'noun'
  | 'verb'
  | 'adjective'
  | 'adverb'
  | 'pronoun'
  | 'preposition'
  | 'article'
  | 'conjunction'
  | 'interjection'
  | 'phrase'

export type DictSense = {
  id: string
  pt: string
  en: string
  pos: PartOfSpeech
  gender?: 'm' | 'f'
  gloss: string
  // Definitions are carried in the bulk-imported data but not shown in the UI.
  // Optional so hand-authored entries don't need filler for a hidden field.
  definitionEn?: string
  definitionPt?: string
  example?: { pt: string; en: string }
  infinitive?: string // verbs only — links into verbs.json for the conjugation table
  feminine?: string // adjectives only — the feminine form (novo → nova), shown on the card
  frequency: number // higher = more common; breaks ties in ranking
  note?: string // usage note for confusable words (ser vs estar, bom vs bem…)
}

export const DICTIONARY = dictionaryData.senses as DictSense[]

function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt')
    .trim()
}

// Reverse conjugation index: a normalized conjugated form → the infinitive(s)
// it belongs to. Built once, lazily, from every single-word verb headword in
// the dictionary (via the same rule-based conjugator the Grammar tab uses), so
// a search for "correu" or "fizemos" surfaces "correr" / "fazer" instead of
// nothing. Only consulted when a query has no direct match, so it never adds
// noise to words that are also a real headword ("casa", "como").
let FORM_TO_INFINITIVE: Map<string, Set<string>> | null = null
function formIndex(): Map<string, Set<string>> {
  if (FORM_TO_INFINITIVE) return FORM_TO_INFINITIVE
  const map = new Map<string, Set<string>>()
  const done = new Set<string>()
  for (const sense of DICTIONARY) {
    if (sense.pos !== 'verb') continue
    const infinitive = sense.infinitive ?? sense.pt
    if (infinitive.includes(' ') || done.has(infinitive)) continue
    done.add(infinitive)
    const grid = conjugationGridFor(infinitive)
    if (!grid) continue
    const infinitiveNorm = normalize(infinitive)
    for (const row of Object.values(grid)) {
      for (const form of Object.values(row ?? {})) {
        for (const token of (form ?? '').split(/\s+/)) {
          const key = normalize(token)
          if (!key || key === 'nao' || key === infinitiveNorm) continue
          let set = map.get(key)
          if (!set) map.set(key, (set = new Set()))
          set.add(infinitive)
        }
      }
    }
    for (const form of extraVerbForms(infinitive)) {
      const key = normalize(form)
      if (key === infinitiveNorm) continue
      let set = map.get(key)
      if (!set) map.set(key, (set = new Set()))
      set.add(infinitive)
    }
  }
  FORM_TO_INFINITIVE = map
  return map
}

// Portuguese words that pluralize outside the regular rules below, keyed by
// singular. Covers every exception actually present in the dictionary — not
// a general-purpose morphological rule (see the Grammar tab's Plurals cheat
// sheet for the taught rules this mirrors).
const PLURAL_IRREGULAR: Record<string, string> = {
  mão: 'mãos',
  irmão: 'irmãos',
  cidadão: 'cidadãos',
  sótão: 'sótãos',
  chão: 'chãos',
  corrimão: 'corrimãos',
  grão: 'grãos',
  pão: 'pães',
  gol: 'gols',
  juiz: 'juízes',
  raiz: 'raízes',
  gás: 'gases',
  país: 'países',
  hífen: 'hifens',
  design: 'designs',
  designer: 'designers',
}

// Best-effort Portuguese pluralization — just enough to build a reverse index
// for search (below), not a general-purpose morphology engine. Returns null
// for endings with no confident rule (bare -s/-x is usually already
// invariant or already plural, e.g. "lápis", "óculos").
function pluralOf(word: string): string | null {
  const irregular = PLURAL_IRREGULAR[word]
  if (irregular) return irregular
  // Checked before the general vowel rule below — "ão" itself ends in "o".
  if (word.endsWith('ão')) return `${word.slice(0, -2)}ões`
  // -ês → -eses: inglês → ingleses, chinês → chineses, português → portugueses.
  if (word.endsWith('ês')) return `${word.slice(0, -2)}eses`
  if (/[aeiouáéíóúâêôãõ]$/i.test(word)) return `${word}s`
  if (/[rz]$/i.test(word)) return `${word}es`
  if (word.endsWith('m')) return `${word.slice(0, -1)}ns`
  if (word.endsWith('il')) {
    const stem = word.slice(0, -2)
    // A written accent earlier in the stem marks the paroxytone class
    // (fácil → fáceis); no accent means it's stressed on -il (funil → funis).
    return /[áàâãéêíóôõú]/i.test(stem) ? `${stem}eis` : `${stem}is`
  }
  const softL = word.match(/^(.*)([aeou])l$/i)
  if (softL) {
    const ending: Record<string, string> = { a: 'ais', e: 'éis', o: 'óis', u: 'uis' }
    return `${softL[1]}${ending[softL[2].toLocaleLowerCase('pt')]}`
  }
  return null
}

// A noun + adjective phrase agrees all the way through: "batata frita" →
// "batatas fritas", "comida chinesa" → "comidas chinesas". With "de / do / em …"
// only the head noun pluralises: "camisa de manga curta" → "camisas de manga
// curta", "fim de semana" → "fins de semana". Any other linking word ("passo a
// passo", "pão e queijo") isn't worth guessing, so those get no plural form.
const HEAD_ONLY_LINKERS = new Set(['de', 'da', 'do', 'das', 'dos', 'em', 'para', 'com'])
const OTHER_LINKERS = new Set(['a', 'o', 'as', 'os', 'e', 'ou', 'por', 'no', 'na'])
function pluralOfPhrase(phrase: string): string | null {
  const words = phrase.split(' ')
  const lower = words.map((word) => word.toLocaleLowerCase('pt'))
  if (lower.some((word) => OTHER_LINKERS.has(word))) return null
  if (lower.some((word) => HEAD_ONLY_LINKERS.has(word))) {
    const head = pluralOf(words[0])
    return head ? [head, ...words.slice(1)].join(' ') : null
  }
  const plurals = words.map((word) => pluralOf(word))
  return plurals.every((plural) => plural !== null) ? plurals.join(' ') : null
}

// Reverse plural index: a normalized plural form → the singular headword(s)
// it belongs to. Built once, lazily, from every noun/adjective in the
// dictionary (masculine and feminine forms alike), so a search for
// "corações" or "professoras" surfaces "coração" / "professor" instead of
// nothing. Only consulted when a query has no direct match (see collect()).
let PLURAL_TO_SINGULAR: Map<string, Set<string>> | null = null
function pluralIndex(): Map<string, Set<string>> {
  if (PLURAL_TO_SINGULAR) return PLURAL_TO_SINGULAR
  const map = new Map<string, Set<string>>()
  for (const sense of DICTIONARY) {
    if (sense.pos !== 'noun' && sense.pos !== 'adjective') continue
    for (const singular of [sense.pt, sense.feminine]) {
      if (!singular) continue
      const plural = singular.includes(' ') ? pluralOfPhrase(singular) : pluralOf(singular)
      if (!plural) continue
      const key = normalize(plural)
      if (key === normalize(singular)) continue
      let set = map.get(key)
      if (!set) map.set(key, (set = new Set()))
      set.add(singular)
    }
  }
  PLURAL_TO_SINGULAR = map
  return map
}

// Possible singulars of an English plural ("responsibilities" → responsibility,
// "boxes" → box, "dogs" → dog). Over-generates on purpose — each candidate is
// only used if it exactly matches a noun's English headword (see collect()).
function englishSingulars(word: string): string[] {
  const out: string[] = []
  if (word.endsWith('ies') && word.length > 4) out.push(`${word.slice(0, -3)}y`)
  if (word.endsWith('ves') && word.length > 4) out.push(`${word.slice(0, -3)}f`, `${word.slice(0, -3)}fe`)
  if (word.endsWith('es') && word.length > 3) out.push(word.slice(0, -2))
  if (word.endsWith('s') && !word.endsWith('ss') && word.length > 2) out.push(word.slice(0, -1))
  return out
}

// One entry a query resolved to: which sense, which of its two language
// fields matched, and whether the match was accent-exact. `viaForm` is set
// when the query was a conjugated verb form or a plural, and this is its
// base (infinitive, or singular).
type Candidate = {
  sense: DictSense
  field: 'pt' | 'en'
  text: string
  exact: boolean
  viaForm?: string
  viaKind?: 'conjugation' | 'plural'
}

function collect(query: string): Candidate[] {
  const trimmed = query.trim()
  if (!trimmed) return []
  const norm = normalize(trimmed)
  const out: Candidate[] = []
  for (const sense of DICTIONARY) {
    for (const field of ['pt', 'en'] as const) {
      const text = sense[field]
      if (normalize(text) === norm) {
        // A cognate whose pt and en spellings are identical ("hotel", "me")
        // matches on both fields. Keep only the "en" one so it groups with
        // any same-meaning sibling sense on the "en" side (e.g. "mim") instead
        // of splitting into a redundant single-sense group of its own.
        if (field === 'pt' && normalize(sense.en) === norm) continue
        out.push({ sense, field, text, exact: text.toLocaleLowerCase('pt') === trimmed.toLocaleLowerCase('pt') })
      } else if (field === 'en' && sense.pos === 'verb' && normalize(text.replace(/^to\s+/i, '')) === norm) {
        // A verb's English is stored as "to bring" — let a bare "bring" find it.
        out.push({ sense, field, text, exact: false })
      } else if (field === 'pt' && sense.feminine && normalize(sense.feminine) === norm) {
        // "nova" finds the adjective "novo"; the card shows both forms.
        out.push({ sense, field, text, exact: false })
      }
    }
    // A sense can also be found by its gloss when that reads as a plain
    // synonym of the headword, or by any extra term listed in a trailing
    // "(also: home, dwelling)" — so "home" finds "casa" even though its
    // headword is "house". Either way the result points at the real `en`.
    const aliases = sense.gloss.match(/\(also:\s*([^)]+)\)/i)?.[1].split(/,\s*/) ?? []
    const glossHit = normalize(sense.gloss) === norm && normalize(sense.gloss) !== normalize(sense.en)
    const aliasHit = aliases.some(
      (alias) => normalize(alias) === norm || (sense.pos === 'verb' && normalize(alias.replace(/^to\s+/i, '')) === norm),
    )
    if (glossHit || aliasHit) {
      out.push({ sense, field: 'en', text: sense.en, exact: false })
    }
  }

  // Nothing matched directly — try reading the query as a conjugated verb form
  // or a plural, and surface its base word.
  if (out.length === 0) {
    const infinitives = formIndex().get(norm)
    if (infinitives) {
      for (const sense of DICTIONARY) {
        if (sense.pos === 'verb' && infinitives.has(sense.infinitive ?? sense.pt)) {
          out.push({ sense, field: 'pt', text: sense.pt, exact: false, viaForm: trimmed, viaKind: 'conjugation' })
        }
      }
    }
    // An English plural ("responsibilities") finds the singular noun's card.
    const enSingulars = new Set(englishSingulars(norm))
    if (enSingulars.size > 0) {
      for (const sense of DICTIONARY) {
        if (sense.pos === 'noun' && enSingulars.has(normalize(sense.en))) {
          out.push({ sense, field: 'en', text: sense.en, exact: false, viaForm: trimmed, viaKind: 'plural' })
        }
      }
    }
    const singulars = pluralIndex().get(norm)
    if (singulars) {
      for (const sense of DICTIONARY) {
        if (
          (sense.pos === 'noun' || sense.pos === 'adjective') &&
          (singulars.has(sense.pt) || (sense.feminine ? singulars.has(sense.feminine) : false))
        ) {
          out.push({ sense, field: 'pt', text: sense.pt, exact: false, viaForm: trimmed, viaKind: 'plural' })
        }
      }
    }
  }
  return out
}

// A result grouped by its exact headword spelling. Two senses that share the
// identical spelling (manga: fruit vs. sleeve) group together as one entry
// with alternate senses; different spellings (pais vs. país) stay separate
// groups, which is what drives the "possible results" panel.
export type ResultGroup = {
  key: string
  field: 'pt' | 'en'
  text: string
  exact: boolean
  senses: DictSense[]
  frequency: number
  viaForm?: string // the conjugated or plural form the user typed, when this group is its base word
  viaKind?: 'conjugation' | 'plural'
}

function groupCandidates(candidates: Candidate[]): ResultGroup[] {
  const byKey = new Map<string, ResultGroup>()
  for (const candidate of candidates) {
    // Case-folded so "cool" and "Cool" are one entry, but accent-preserving
    // so avó and avô stay apart.
    const key = `${candidate.field}:${candidate.text.toLocaleLowerCase('pt')}`
    let group = byKey.get(key)
    if (!group) {
      group = { key, field: candidate.field, text: candidate.text, exact: false, senses: [], frequency: 0 }
      byKey.set(key, group)
    }
    group.senses.push(candidate.sense)
    group.exact = group.exact || candidate.exact
    group.frequency = Math.max(group.frequency, candidate.sense.frequency)
    group.viaForm = group.viaForm ?? candidate.viaForm
    group.viaKind = group.viaKind ?? candidate.viaKind
  }
  const groups = [...byKey.values()]
  for (const group of groups) {
    group.senses.sort((a, b) => b.frequency - a.frequency)
    // Show the spelling of the most common sense (e.g. "cool", not "Cool").
    group.text = group.senses[0][group.field]
  }
  // Exact spelling ranks above accent-insensitive; ties break by frequency.
  groups.sort((a, b) => (a.exact !== b.exact ? (a.exact ? -1 : 1) : b.frequency - a.frequency))
  return groups
}

// The longest phrase headword that appears verbatim inside the query — for
// "the input contains a known idiom" rather than "the input IS the idiom".
// The words of a string, accents and case ignored: "Fazer check-in!" → ["fazer", "check", "in"].
function wordsOf(text: string): string[] {
  return normalize(text).split(/[^a-z0-9]+/).filter(Boolean)
}

// True when `needle` appears in `haystack` as a run of whole words, so the phrase "tá" is found in
// "você tá bem" but not inside "pirata".
function containsWords(haystack: string[], needle: string[]): boolean {
  if (needle.length === 0 || needle.length > haystack.length) return false
  for (let start = 0; start + needle.length <= haystack.length; start += 1) {
    if (needle.every((word, offset) => haystack[start + offset] === word)) return true
  }
  return false
}

function findPhraseCandidate(query: string): Candidate | null {
  const queryWords = wordsOf(query)
  let best: Candidate | null = null
  for (const sense of DICTIONARY) {
    if (sense.pos !== 'phrase') continue
    for (const field of ['pt', 'en'] as const) {
      const text = sense[field]
      if (!containsWords(queryWords, wordsOf(text))) continue
      if (!best || normalize(text).length > normalize(best.text).length) best = { sense, field, text, exact: false }
    }
  }
  return best
}

const SENTENCE_WORD_THRESHOLD = 5

// A rough heuristic for "this isn't a word/phrase lookup query" — too many
// words, or punctuation that isn't just a single trailing ?/.
export function looksLikeSentence(query: string): boolean {
  const trimmed = query.trim()
  if (!trimmed) return false
  if (trimmed.split(/\s+/).filter(Boolean).length > SENTENCE_WORD_THRESHOLD) return true
  return /[,;:.!?]/.test(trimmed.replace(/[?.!]+$/, ''))
}

function levenshtein(a: string, b: string): number {
  const dp: number[][] = Array.from({ length: a.length + 1 }, (_, i) => [i, ...new Array(b.length).fill(0)])
  for (let j = 0; j <= b.length; j += 1) dp[0][j] = j
  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1])
    }
  }
  return dp[a.length][b.length]
}

const MAX_SUGGESTION_DISTANCE = 2

// "Did you mean…" candidates for a query with no exact/loose match, ranked
// by edit distance then frequency (PRD 5.5) — reuses the same ResultGroup
// shape as a normal match so the UI needs only one rendering path.
export function editDistanceSuggestions(query: string, limit: number): ResultGroup[] {
  const norm = normalize(query)
  const byKey = new Map<string, { field: 'pt' | 'en'; text: string; distance: number; senses: DictSense[] }>()
  for (const sense of DICTIONARY) {
    for (const field of ['pt', 'en'] as const) {
      const text = sense[field]
      const distance = levenshtein(norm, normalize(text))
      if (distance === 0 || distance > MAX_SUGGESTION_DISTANCE) continue
      const key = `${field}:${text}`
      const entry = byKey.get(key) ?? { field, text, distance, senses: [] }
      entry.senses.push(sense)
      byKey.set(key, entry)
    }
  }
  const entries = [...byKey.values()].sort((a, b) => {
    if (a.distance !== b.distance) return a.distance - b.distance
    return Math.max(...b.senses.map((s) => s.frequency)) - Math.max(...a.senses.map((s) => s.frequency))
  })
  return entries.slice(0, limit).map((entry) => ({
    key: `${entry.field}:${entry.text}`,
    field: entry.field,
    text: entry.text,
    exact: false,
    senses: entry.senses.sort((a, b) => b.frequency - a.frequency),
    frequency: Math.max(...entry.senses.map((s) => s.frequency)),
  }))
}

export type LookupResult =
  | { type: 'sentence' }
  | { type: 'none'; query: string }
  | { type: 'suggestions'; query: string; groups: ResultGroup[] }
  | { type: 'match'; groups: ResultGroup[]; ambiguous: boolean }

// Match order per PRD 5.1: (1) exact/accent-insensitive match against the
// full index, (2) a known phrase appearing inside the query, (3) a
// sentence-length fallback, else (5.5) edit-distance suggestions or "no
// results".
export function lookup(query: string): LookupResult {
  const trimmed = query.trim()
  if (!trimmed) return { type: 'none', query: trimmed }

  const exact = collect(trimmed)
  if (exact.length > 0) {
    const groups = groupCandidates(exact)
    return { type: 'match', groups, ambiguous: groups.length > 1 }
  }

  const phrase = findPhraseCandidate(trimmed)
  if (phrase) return { type: 'match', groups: groupCandidates([phrase]), ambiguous: false }

  if (looksLikeSentence(trimmed)) return { type: 'sentence' }

  const suggestions = editDistanceSuggestions(trimmed, 3)
  if (suggestions.length > 0) return { type: 'suggestions', query: trimmed, groups: suggestions }
  return { type: 'none', query: trimmed }
}

// `translation` is the other-language headword — the meaning shown next to a
// suggestion, rather than the descriptive gloss.
export type AutocompleteCandidate = { text: string; field: 'pt' | 'en'; translation: string; pos: PartOfSpeech }

// Search-as-you-type dropdown: prefix match against both languages, ranked
// by frequency, deduplicated by headword spelling.
export function autocomplete(query: string, limit = 8): AutocompleteCandidate[] {
  const norm = normalize(query)
  if (!norm) return []
  // One row per headword spelling (case-folded); when spellings collide, keep
  // the most common sense's translation and part of speech.
  const byKey = new Map<string, AutocompleteCandidate & { frequency: number }>()
  for (const sense of DICTIONARY) {
    for (const field of ['pt', 'en'] as const) {
      const text = sense[field]
      // A cognate whose pt and en spellings are identical ("hotel") would
      // otherwise list itself twice — once per field. Keep only the "en" row.
      if (field === 'pt' && normalize(sense.en) === normalize(sense.pt)) continue
      // A verb's English is "to bring" (also match "bri…"); an adjective's
      // feminine form should surface its masculine headword ("nova" → "novo").
      const bare = field === 'en' && sense.pos === 'verb' ? text.replace(/^to\s+/i, '') : text
      const alt = field === 'pt' && sense.feminine ? sense.feminine : text
      // A trailing "(also: few, hot)" in the gloss is a search alias for a
      // synonym with no headword of its own — typing that synonym should
      // still surface this sense in the dropdown, same as collect() does.
      const aliases = field === 'en' ? (sense.gloss.match(/\(also:\s*([^)]+)\)/i)?.[1].split(/,\s*/) ?? []) : []
      const aliasMatch = aliases.some(
        (alias) => normalize(alias).startsWith(norm) || (sense.pos === 'verb' && normalize(alias.replace(/^to\s+/i, '')).startsWith(norm)),
      )
      if (!aliasMatch && ![text, bare, alt].some((candidate) => normalize(candidate).startsWith(norm))) continue
      const key = `${field}:${text.toLocaleLowerCase('pt')}`
      const existing = byKey.get(key)
      if (!existing || sense.frequency > existing.frequency) {
        byKey.set(key, {
          text,
          field,
          translation: field === 'pt' ? sense.en : sense.pt,
          pos: sense.pos,
          frequency: sense.frequency,
        })
      }
    }
  }
  const out = [...byKey.values()].sort((a, b) => b.frequency - a.frequency)
  return out.slice(0, limit).map(({ frequency: _frequency, ...candidate }) => candidate)
}

// Word-by-word breakdown for sentence-length input (PRD 7, "nice to have").
// Never implies grammar or word order — just the best guess per token.
export function wordByWordBreakdown(query: string): Array<{ word: string; group: ResultGroup | null }> {
  return query
    .trim()
    .split(/\s+/)
    .map((raw) => raw.replace(/^[^\p{L}]+|[^\p{L}]+$/gu, ''))
    .filter(Boolean)
    .map((word) => {
      const groups = groupCandidates(collect(word))
      return { word, group: groups[0] ?? null }
    })
}
